import React, { useEffect, useState } from "react";
import { Modal, Button, Tab, Container, Form } from "react-bootstrap";
import Tabs from 'react-bootstrap/Tabs';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {faExclamationCircle, faInfoCircle, faEye, faQuestionCircle} from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { defaultFormState } from "../../../../defaults/Forms";
import {employeeCompactedDefault, employeeDefault} from "../../../../defaults/Items";
import {
  compareData,
  restoreFormData,
  onCloseDetails
} from "../../../../helpers/ComponentHelper";

import AppComponentCardHeader from "../../common/AppComponentCardHeader";
import LoadingDataDiv from "../../common/LoadingDataDiv";
import DeleteWarningModal from "../../common/DeleteWarningModal";
import useCrud from "../../../../hooks/useCrud";
import { Table } from "../../../table/Table";
import TabEmployeePersonalData from "./TabEmployeePersonalData";
import TabEmployeeCompanyData from "./TabEmployeeCompanyData";
import {ToastContainer} from "react-toastify";
import {toastError, toastSuccess} from "../../../../helpers/Toast";
import TabEmployeeWorkLog from "./TabEmployeeWorkLog";
import EmployeeDocumentsCard from "./EmployeeDocumentsCard";
import EmployeeAssignmentTools from "./EmployeeAssignmentTools";
import useAuth from "../../../../hooks/useAuth";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import {PERMISSIONS as P, hasPermission} from "../../../../auth/permissions";
import "../../../../css/UserManagement.css";
import "../../../../css/DictionaryForms.css";
import "../../../../css/ResourceForms.css";

const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%]).{8,24}$/; // requires 1 uppercase letter, 1 lowercase letter, 1 digit, 1 special char and has to be 8-24 chars long

const EmployeeTabQuickAccess = ({items, tabKey, onNavigate}) => (
  <div className="employee-tab-quick-access">
    <span>W tej zakładce:</span>
    <div className="employee-tab-quick-links">
      {items.map(item => (
        <a
          key={item.id}
          href={`#${item.id}`}
          className="quick-access-menu-link"
          onClick={event => onNavigate(event, item.id, tabKey)}
        >
          {item.label}
        </a>
      ))}
    </div>
  </div>
);

const PERSONAL_QUICK_LINKS = [
  {id: "basic-data", label: "Dane podstawowe"},
  {id: "contact-communication", label: "Kontakt i komunikacja"},
  {id: "employee-status-settlements", label: "Status i rozliczenia"},
  {id: "identity-data", label: "Dane identyfikacyjne"},
  {id: "addresses", label: "Adresy"},
  {id: "account-access", label: "Konto i dostęp"},
  {id: "personal-photo", label: "Zdjęcie"}
];

const COMPANY_QUICK_LINKS = [
  {id: "employment-organization", label: "Zatrudnienie"},
  {id: "positions", label: "Stanowiska"},
  {id: "company-roles", label: "Role"},
  {id: "soft-skills", label: "Kompetencje"},
  {id: "trainings", label: "Szkolenia"},
  {id: "certifications", label: "Certyfikaty"},
  {id: "evaluations", label: "Oceny"},
  {id: "health-absence", label: "Zdrowie i nieobecności"},
  {id: "working-time", label: "Czas pracy"},
  {id: "employee-settlements", label: "Rozliczenia"},
  {id: "business-trips", label: "Wyjazdy służbowe"}
];

const Employee = ({employeeId = null, embedded = false, onEmbeddedClose = null, onEmbeddedSaved = null}) => {

  const { auth } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const { id: routeEmployeeId } = useParams();
  const id = employeeId ?? routeEmployeeId;

  const dataUrl = "/employees";
  const maritalStatusesUrl="/marital-statuses";
  const gendersUrl = "/genders";
  const nfzBranchesUrl = "/nfz-branches";
  const timeCardsUrl = "/time-cards";
  const taxOfficesUrl = "/tax-offices";
  const hiringStagesUrl = "/hiring-stages";
  const countriesUrl = "/countries";
  const identityCardTypesUrl = "/identity-card-types";
  const settingsUrl = "/settings";
  const dataFullUrl = "/employees";
  const rolesUrl = "/admin/role";
  const softSkillsUrl = "/soft-skills";
  const trainingsUrl = "/trainings";
  const commendationsUrl = "/commendations";
  const infractionsUrl = "/infractions";
  const certificationTypesUrl = "/certification-types";
  const medexamTypesUrl = "/medical-examination-types";
  const absenceTypesUrl = "/absence-types";
  const departmentsUrl = "/departments";
  const positionsUrl = "/positions";
  const departmentPositionsUrl = "/departments/id/positions";
  const hiringCompaniesUrl = "/business-partners/hiring-companies";
  const employeeColumnPreferencesUrl = "/user-preferences/employee-list-columns";

  const defaultItem = employeeCompactedDefault;
  const defaultFullItem = employeeDefault;
  const itemName = "pracownik";
  const itemNames = "pracownicy";

  const navigate = useNavigate();
  const location = useLocation();
  const { createItem, updateItem, deleteItem, getItems, getRelatedChildrenByParentId } = useCrud(dataUrl);

  const [loading, setLoading] = useState(true);
  const [allowDelete, setAllowDelete] = useState(null);
  const [currentFormState, setCurrentFormState] = useState(defaultFormState);
  const [itemsList, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState(defaultFullItem);
  const [backupItem, setBackupItem] = useState(defaultItem);
  const [itemChanged, setItemChanged] = useState(false);
  const [rolesList, setRolesList] = useState([]);
  const [softSkillsList, setSoftSkillsList] = useState([]);
  const [trainingsList, setTrainingsList] = useState([]);
  const [commendationsList, setCommendationsList] = useState([]);
  const [infractionsList, setInfractionsList] = useState([]);
  const [validPassword, setValidPassword] = useState(true);
  const [updateConflict, setUpdateConflict] = useState(false);
  const [oldPasswordValidationPassed, setOldPasswordValidationPassed] = useState(false);
  const [maritalStatusesList, setMaritalStatuses] = useState([]);
  const [gendersList, setGenders] = useState([]);
  const [nfzBranchesList, setNfzBranches] = useState([]);
  const [taxOfficesList, setTaxOffices] = useState([]);
  const [hiringStagesList, setHiringStages] = useState([]);
  const [countriesList, setCountries] = useState([]);
  const [identityCardTypesList, setIdentityCardTypes] = useState([]);
  const [certificationTypesList, setCertificationTypesList] = useState([]);
  const [medExamTypesList, setMedExamTypesList] = useState([]);
  const [absenceTypesList, setAbsenceTypesList] = useState([]);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [timeCardsList, setTimeCardsList] = useState([]);
  const [positionsList, setPositionsList] = useState([]);
  const [settingsList, setSettingsList] = useState([]);
  const [hiringCompaniesList, setHiringCompaniesList] = useState([]);
  // const [displayedManager, setDisplayedManager] = useState([]);
  // const [displayedDeputyName, setDisplayedDeputyName] = useState([]);
  const [activeTab, setActiveTab] = useState("employee-personal-data");
  const [showDeptGuard, setShowDeptGuard] = useState(false);


  // elements related to the item


  useEffect(() => {
    //console.log(commendationsList);
  }, commendationsList)


  const flattenDepartmentNames = item => {
    const names = Array.isArray(item?.departmentNames)
      ? item.departmentNames
      : [];

    const normalizedNames = names
      .map(name => String(name || "").trim())
      .filter(Boolean);

    if (normalizedNames.length > 0) {
      return [...new Set(normalizedNames)].join("; ");
    }

    return String(item?.department?.name || "").trim();
  };

  const flattenPositionNames = item => {
    const names = Array.isArray(item?.positionNames) ? item.positionNames : [];
    return [...new Set(names.map(name => String(name || "").trim()).filter(Boolean))].join("; ");
  };

  const prepareEmployeeListItem = item => ({
    ...item,
    displayName: [item.firstName, item.lastName].filter(Boolean).join(" "),
    employeeStatus: [
      item.pupil ? "Uczeń" : null,
      item.student ? "Student" : null,
      item.krus ? "KRUS" : null,
      item.unwanted ? "Niechciany" : null
    ].filter(Boolean).join(" · ") || "Standard",
    departmentNamesText: flattenDepartmentNames(item),
    positionNamesText: flattenPositionNames(item),
    archiveStatus: item.archived ? "Archiwum" : "Aktywny"
  });

  const authRoles = Array.isArray(auth?.roles)
    ? auth.roles
    : typeof auth?.roles === "string"
      ? auth.roles.replace(/[\[\]\s]/g, "").split(",")
      : [];
  const canDeleteEmployees = authRoles.includes("ROLE_ADMIN");
  const canViewEmployeeDocuments = hasPermission(auth?.permissions, P.EMPLOYEE_DOCUMENTS_VIEW);
  const canManageEmployeeDocuments = hasPermission(auth?.permissions, P.EMPLOYEE_DOCUMENTS_MANAGE);
  const canArchiveEmployees = authRoles.includes("ROLE_ADMIN") || authRoles.includes("ROLE_HR");
  const [archiveFilter, setArchiveFilter] = useState("ACTIVE");
  const [managementFilter, setManagementFilter] = useState("ALL");
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const defaultVisibleColumnAccessors = [
    "displayName",
    "login",
    "departmentNamesText",
    "positionNamesText",
    "hiringStage",
    "employeeSkills",
    "employeeStatus",
    "contractStatus"
  ];
  const [visibleColumnAccessors, setVisibleColumnAccessors] = useState(defaultVisibleColumnAccessors);

  const departmentFilterOptions = departmentsList
    .map(department => department?.name)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, "pl"));

  const positionFilterOptions = selectedDepartment => positionsList
    .filter(position => !selectedDepartment || position?.department?.name === selectedDepartment)
    .map(position => position?.name)
    .filter(Boolean)
    .filter((name, index, array) => array.indexOf(name) === index)
    .sort((a, b) => a.localeCompare(b, "pl"));

  const baseColumns = [
    {label: "Id", accessor: "id", sortable: true, searchable: false, visible: false},
    {label: "Pracownik", accessor: "displayName", sortable: true, searchable: true},
    {label: "Login", accessor: "login", sortable: true, searchable: true},
    {
      label: "Działy",
      accessor: "departmentNamesText",
      sortable: true,
      searchable: true,
      type: "options",
      options: departmentFilterOptions,
      clearsFilters: ["positionNamesText"]
    },
    {
      label: "Stanowiska",
      accessor: "positionNamesText",
      sortable: true,
      searchable: true,
      type: "options",
      optionsFactory: ({filters}) => positionFilterOptions(filters.departmentNamesText)
    },
    {label: "Etap zatrudnienia", accessor: "hiringStage", sortable: true, searchable: true, format: hiringStage => hiringStage ? `${hiringStage.name}` : "", fieldsToInclude: ["name"]},
    {label: "Umiejętności", accessor: "employeeSkills", sortable: true, searchable: true},
    {label: "Status", accessor: "employeeStatus", sortable: true, searchable: true, fieldsToInclude: ["employeeStatus"]},
    {
      label: "Status umowy",
      accessor: "contractStatus",
      sortable: true,
      searchable: true,
      colorsMap: "V=#008000;E=#FFA500;X=#808080;N=#FF0000",
      contentMap: "V='aktywna';E='wygasa';X='wygasła';N='brak umowy'",
      type: "optionsMap",
      optionsMap: ["brak umowy->N", "wygasa->E", "wygasła->X", "aktywna->V"]
    },
    {label: "Archiwum", accessor: "archiveStatus", sortable: true, searchable: true, visible: false},
    {label: "Edycja", accessor: "editBtn", sortable: false, searchable: false},
    ...(canDeleteEmployees ? [{label: "Usuń", accessor: "deleteBtn", sortable: false, searchable: false}] : [])
  ];

  const configurableColumns = baseColumns.filter(column => !["id", "editBtn", "deleteBtn", "archiveStatus"].includes(column.accessor));
  const columns = baseColumns.map(column => {
    if (["editBtn", "deleteBtn"].includes(column.accessor)) return {...column, visible: true};
    if (column.accessor === "id" || column.accessor === "archiveStatus") return {...column, visible: false};
    return {...column, visible: visibleColumnAccessors.includes(column.accessor)};
  });

  const saveEmployeeColumnPreferences = async columnsToSave => {
    try {
      await axiosPrivate.put(employeeColumnPreferencesUrl, columnsToSave);
    } catch (_) {
      toastError("Nie udało się zapisać konfiguracji kolumn użytkownika.");
    }
  };

  const toggleEmployeeColumn = accessor => {
    const next = visibleColumnAccessors.includes(accessor)
      ? visibleColumnAccessors.filter(item => item !== accessor)
      : [...visibleColumnAccessors, accessor];
    setVisibleColumnAccessors(next);
    saveEmployeeColumnPreferences(next);
  };

  const exportEmployeeRows = (rows, currentColumns) => {
    const exportColumns = currentColumns.filter(column => column.visible !== false && !["editBtn", "deleteBtn"].includes(column.accessor));

    return rows.map(row => Object.fromEntries(exportColumns.map(column => {
      let value = row[column.accessor];

      if (column.accessor === "departmentNamesText") {
        value = row.departmentNamesText || flattenDepartmentNames(row);
      } else if (column.accessor === "positionNamesText") {
        value = row.positionNamesText || flattenPositionNames(row);
      } else if (column.format) {
        value = column.format(value);
      }

      if (column.accessor === "contractStatus") {
        value = {V: "aktywna", E: "wygasa", X: "wygasła", N: "brak umowy"}[value] || value || "";
      }

      if (Array.isArray(value)) {
        value = value.map(item => typeof item === "object" ? (item?.name || item?.shortName || "") : item).filter(Boolean).join("; ");
      } else if (value && typeof value === "object") {
        value = value.name || value.shortName || "";
      }

      return [column.label, value ?? ""];
    })));
  };

  const onDelete = async () => {
    const response = await deleteItem(`${dataFullUrl}/${currentItem.id}`, currentItem.id, state);
    if (!response) return;
    if (response.status === 401 || response.status === 403) {
      navigate('/login', {state: {from: location}, replace: true});
    } else if (response.status === 409 || response.status === 422) {
      toastError(response.data || "Nie można usunąć pracownika, ponieważ posiada powiązane dane.");
      setCurrentFormState(prev => ({
        ...prev,
        showDeleteWarning: false,
        warningDeleteButtonDisabled: false,
        warningWarningIconVisible: false
      }));
    }
  }

  const validatePassword = (newPassword) => {
    //console.log("VALIDATING");

    if (currentFormState.formAddingDataMode && newPassword === "") {
      setValidPassword(false);
      //console.log("Hasło nie może być puste")
      return;
    }

    // TODO Remove 123 and ABC from valid cases
    if ((newPassword && !PWD_REGEX.test(newPassword)) && newPassword !== "123" && newPassword !== "ABC") {
      setValidPassword(false);
      //console.log("Hasło nie spełnia warunków");
      return false;
    }
    return true;
  }


  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)


  const onSaveItemClick = async (e) => {
    e.preventDefault();

    // console.log(currentItem.contractReference);


    const existingAppUser = !currentFormState.formAddingDataMode && !!backupItem.appUser;
    const appUser = !!backupItem.appUser || !!currentItem.appUser;
    const oldPwdField = document.getElementById("oldPassword");
    const passwordField = document.getElementById("password");
    const passwordConfirmationField = document.getElementById("passwordConf");
    const oldPwd = oldPwdField ? oldPwdField.value : "";
    const newPassword = passwordField ? passwordField.value : "";
    const passwordConfirmation = passwordConfirmationField ? passwordConfirmationField.value : "";
    const addingAccount = appUser && (currentFormState.formAddingDataMode || !existingAppUser);
    const changingPassword = existingAppUser && !!currentItem.password;
    const passwordValidationRequired = addingAccount || changingPassword;
    const oldPasswordRequired = changingPassword;
    const newAccountPasswordRequired = addingAccount;

    if(oldPasswordRequired && oldPwd === "") {
      let nameInput = document.getElementById("oldPassword");
      nameInput.classList.add("form-input-invalid");
      nameInput.placeholder = `ten element jest wymagany`;
      return;
    }

    if(newAccountPasswordRequired && !newPassword) {
      let passwordInput = document.getElementById("password");
      passwordInput.classList.add("form-input-invalid");
      passwordInput.placeholder = `ten element jest wymagany`;
      toastError("Uzupełnij pole Hasło");
      return;
    }

    if(passwordValidationRequired && newPassword !== passwordConfirmation) {
      let passwordConfirmationInput = document.getElementById("passwordConf");
      passwordConfirmationInput.classList.add("form-input-invalid");
      toastError("Podane hasła są różne");
      return;
    }

    if(passwordValidationRequired && !validPassword) {
      let passwordInput = document.getElementById("password");
      passwordInput.classList.add("form-input-invalid");
      toastError("Hasło nie spełnia wymagań");
      return;
    }

    if (appUser && !(currentItem.userRoleDTOS || currentItem.userRoles || []).length) {
      toastError("Użytkownik aplikacji musi mieć przypisaną przynajmniej jedną rolę");
      return;
    }

    if(!currentItem.login || currentItem.login.length < 4) {
      //console.log("błąd login < 4 or null");
      let nameInput = document.getElementById("login");
      nameInput.classList.add("form-input-invalid");
      nameInput.placeholder = `ten element jest wymagany`;
      return;
    }

    //if(!currentItem.login || !currentItem.companyEmail || !currentItem.firstName || !currentItem.lastName || !currentItem.dateOfBirth || !currentItem.gender || !currentItem.department ) {
    let formHasErrors = false;

      if(!currentItem.login) {
        let loginInput = document.getElementById("login");
        loginInput.classList.add("form-input-invalid");
        loginInput.placeholder = `ten element jest wymagany`;
        formHasErrors = true;
        toastError("Uzupełnij pole Login");
      }
      if(!currentItem.companyEmail) {
        let emailInput = document.getElementById("companyEmail");
        emailInput.classList.add("form-input-invalid");
        emailInput.placeholder = `ten element jest wymagany`;
        formHasErrors = true;
        toastError("Uzupełnij pole Firmowy email");
      }

      if (currentItem.companyEmail && !isValidEmail(currentItem.companyEmail)) {
        let emailInput = document.getElementById("companyEmail");
        emailInput.classList.add("form-input-invalid");
        emailInput.placeholder = `ten element jest wymagany`;
        formHasErrors = true;
        toastError("Firmowy email nie jest prawidłowym adresem");
      }


      if(!currentItem.firstName) {
        //console.log("firstName jest null");
        let firstNameInput = document.getElementById("firstName");
        firstNameInput.classList.add("form-input-invalid");
        firstNameInput.placeholder = `ten element jest wymagany`;
        formHasErrors = true;
        toastError("Uzupełnij pole Imię");
      }
      if(!currentItem.lastName) {
        let lastNameInput = document.getElementById("lastName");
        lastNameInput.classList.add("form-input-invalid");
        lastNameInput.placeholder = `ten element jest wymagany`;
        formHasErrors = true;
        toastError("Uzupełnij pole Nazwisko");
      }
      if(!currentItem.dateOfBirth) {
        //console.log("błąd DOB mull");
        let birthDateInput = document.getElementById("dateOfBirth");
        birthDateInput.classList.add("form-input-invalid");
        birthDateInput.placeholder = `ten element jest wymagany`;
        formHasErrors = true;
        toastError("Uzupełnij pole Data urodzenia");
      }
      if(!currentItem.gender) {
        //console.log("błąd gender mull");
        let genderInput = document.getElementById("gender");
        genderInput.classList.add("form-input-invalid");
        genderInput.placeholder = `ten element jest wymagany`;
        formHasErrors = true;
        toastError("Uzupełnij pole Płeć");
      }

      const selectedHiringStage = hiringStagesList.find(
        stage => Number(stage?.id || 0) === Number(currentItem.hiringStage?.id || 0)
      ) || currentItem.hiringStage;
      const departmentRequired = !!selectedHiringStage?.departmentRequired;
      const positionRequired = departmentRequired && !!selectedHiringStage?.positionRequired;
      const departmentId = Number(currentItem.department?.id || 0);
      const hasPrimaryDepartment = departmentId > 0;

      if (departmentRequired && !hasPrimaryDepartment) {
        formHasErrors = true;
        setActiveTab("employee-company-data");
        toastError(`Etap zatrudnienia ${selectedHiringStage?.name || ""} wymaga przypisania działu`);
      }

      if (positionRequired && hasPrimaryDepartment) {
        const hasPositionInDepartment = (currentItem.employeePositions || []).some(employeePosition => {
          if (employeePosition?.active === false || !employeePosition?.position?.id) return false;
          const fullPosition = positionsList.find(position => Number(position.id) === Number(employeePosition.position.id)) || employeePosition.position;
          return Number(fullPosition?.department?.id || 0) === departmentId;
        });
        if (!hasPositionInDepartment) {
          formHasErrors = true;
          setActiveTab("employee-company-data");
          toastError(`Etap zatrudnienia ${selectedHiringStage?.name || ""} wymaga przypisania stanowiska z wybranego działu`);
        }
      }

      if (currentItem.bankAccountOwnedByEmployee === false) {
        if (!currentItem.bankAccountNumber) {
          document.getElementById("bankAccountNumber")?.classList.add("form-input-invalid");
          formHasErrors = true;
          toastError("Uzupełnij numer rachunku osoby trzeciej");
        }
        if (!currentItem.bankAccountHolderName) {
          document.getElementById("bankAccountHolderName")?.classList.add("form-input-invalid");
          formHasErrors = true;
          toastError("Uzupełnij właściciela rachunku");
        }
        if (!currentItem.bankAccountThirdPartyConsent) {
          formHasErrors = true;
          toastError("Potwierdź zgodę pracownika na wypłatę wynagrodzenia na rachunek osoby trzeciej");
        }
        if (!currentItem.bankAccountConsentDate) {
          document.getElementById("bankAccountConsentDate")?.classList.add("form-input-invalid");
          formHasErrors = true;
          toastError("Uzupełnij datę oświadczenia dotyczącego rachunku osoby trzeciej");
        }
      }

      if (formHasErrors) return;
    //}

    // TODO!
    // if(currentItem.userRoles.length === 0) {
    //   let userRolesDiv = document.getElementById("userRoles");
    //   userRolesDiv.classList.add("form-input-invalid");
    //   return;
    // }


    currentItem.timeCardId = currentItem.timeCard ? currentItem.timeCard.id : null

    let response;

    const item = {
      id: currentItem.id,
      login: currentItem.login,
      appUser: appUser,
      gender: currentItem.gender ? {id: currentItem.gender.id} : null,
      firstName: currentItem.firstName,
      secondName: currentItem.secondName,
      lastName: currentItem.lastName,
      dateOfBirth: currentItem.dateOfBirth,
      placeOfBirth: currentItem.placeOfBirth,
      mothersName: currentItem.mothersName,
      fathersName: currentItem.fathersName,
      maidenName: currentItem.maidenName,
      maritalStatus: currentItem.maritalStatus ? { id: currentItem.maritalStatus.id } : null,
      pesel: currentItem.pesel,
      nip: currentItem.nip,
      primaryPhone: currentItem.primaryPhone,
      secondaryPhone: currentItem.secondaryPhone,
      companyEmail: currentItem.companyEmail,
      privateEmail: currentItem.privateEmail,
      userRoleDTOS: currentItem.userRoleDTOS || currentItem.userRoles || [],
      photos: currentItem.photos,
      createDate: currentItem.createDate,
      lastUpdateDate: currentItem.lastUpdateDate,
      createdBy: currentItem.createdBy,
      updatedBy: currentItem.updatedBy,
      iceContact: currentItem.iceContact,
      bankAccountNumber: currentItem.bankAccountNumber,
      bankAccountOwnedByEmployee: currentItem.bankAccountOwnedByEmployee !== false,
      bankAccountHolderName: currentItem.bankAccountHolderName,
      bankAccountThirdPartyConsent: !!currentItem.bankAccountThirdPartyConsent,
      bankAccountConsentDate: currentItem.bankAccountConsentDate || null,
      nfzBranch: currentItem.nfzBranch ? { id: currentItem.nfzBranch.id } : null,
      taxOffice: currentItem.taxOffice ? { id: currentItem.taxOffice.id } : null,
      student: currentItem.student,
      pupil: currentItem.pupil,
      krus: currentItem.krus,
      studentId: currentItem.studentId,
      unwanted: currentItem.unwanted,
      unwantedReason: currentItem.unwantedReason,
      parentalConsent: currentItem.parentalConsent,
      enabled: currentItem.enabled,
      hiringStage: currentItem.hiringStage ? {id: currentItem.hiringStage.id} : null,
      primaryAddressStreet: currentItem.primaryAddressStreet,
      primaryAddressStreetNumber: currentItem.primaryAddressStreetNumber,
      primaryAddressPostalCode: currentItem.primaryAddressPostalCode,
      primaryAddressCity: currentItem.primaryAddressCity,
      primaryAddressCountry: currentItem.primaryAddressCountry ? {id: currentItem.primaryAddressCountry.id} : null,
      secondaryAddressStreet: currentItem.secondaryAddressStreet,
      secondaryAddressStreetNumber: currentItem.secondaryAddressStreetNumber,
      secondaryAddressPostalCode: currentItem.secondaryAddressPostalCode,
      secondaryAddressCity: currentItem.secondaryAddressCity,
      secondaryAddressCountry: currentItem.secondaryAddressCountry ? {id: currentItem.secondaryAddressCountry.id} : null,
      identityCardType: currentItem.identityCardType ? {id: currentItem.identityCardType.id} : null,
      identityCardId: currentItem.identityCardId,
      identityCardIssueDate: currentItem.identityCardIssueDate,
      identityCardExpiryDate: currentItem.identityCardExpiryDate,
      issuedBy: currentItem.issuedBy,
      issueLocation: currentItem.issueLocation,
      employeePositions: currentItem.employeePositions,
      employeeSoftSkills: currentItem.employeeSoftSkills,
      employeeTrainings: currentItem.employeeTrainings,
      employeeCommendations: currentItem.employeeCommendations,
      employeeInfractions: currentItem.employeeInfractions,
      employeeCertifications: currentItem.employeeCertifications,
      employeeMedExams: currentItem.employeeMedExams,
      employeeAbsences: currentItem.employeeAbsences,
      missingTrainings: currentItem.missingTrainings,
      department: Number(currentItem.department?.id || 0) > 0 ? {id: currentItem.department.id} : null,
      position: currentItem.position,
      communicationByEmail: currentItem.communicationByEmail,
      communicationBySMS: currentItem.communicationBySMS,
      hiringCompany: currentItem.hiringCompany ? {id: currentItem.hiringCompany.id} : null,
      timeCardId: currentItem.timeCardId,
      timeCard: currentItem.timeCard ? {id: currentItem.timeCard.id} : null,
      contractStatus: currentItem.contractStatus,
      contractReference: currentItem.contractReference,
      availableOnlyWeekends: currentItem.availableOnlyWeekends,
      availableOnlyWorkingDays: currentItem.availableOnlyWorkingDays,
      scheduleMinWeeklyHours: currentItem.scheduleMinWeeklyHours,
      scheduleMaxWeeklyHours: currentItem.scheduleMaxWeeklyHours,
      scheduleRequiredDaysOffPerWeek: currentItem.scheduleRequiredDaysOffPerWeek,
      scheduleMaxConsecutiveWorkDays: currentItem.scheduleMaxConsecutiveWorkDays,
      employeeAvailabilities: currentItem.employeeAvailabilities,
      employeeSkills: currentItem.employeeSkills,
      archived: !!currentItem.archived,
      archivedAt: currentItem.archivedAt || null,
      archivedReason: currentItem.archivedReason || null
    }

    if (passwordValidationRequired && currentItem.password) {
      item.password = currentItem.password;
    }

    if (oldPasswordRequired) {
      item.oldPassword = oldPwd;
    }

    //console.log(item);

    if (currentFormState.formAddingDataMode) {
      const itemToSave = {...item};
      delete itemToSave.id;
      response = await createItem(dataFullUrl, itemToSave, state);
    } else {
      //console.log(item);
      response = await updateItem(`${ dataFullUrl }/${ item.id }`, item, state);
    }


    if (response.status === 422) {
      setOldPasswordValidationPassed(false);

      if (response.data?.message === "Old password is invalid") {
        const oldPasswordInput = document.getElementById("oldPassword");
        if (oldPasswordInput) {
          oldPasswordInput.classList.add("form-input-invalid");
          oldPasswordInput.value = "";
          oldPasswordInput.placeholder = "stare hasło jest nieprawidłowe";
        }

        toastError("Stare hasło jest nieprawidłowe");
        return;
      }

      toastError(response.data?.message || "Stare hasło jest nieprawidłowe. Nie udało się zapisać zmian");
      return;
    } else {
      setOldPasswordValidationPassed(true);
    }




    if (response.status === 401 || response.status === 403) {
      navigate('/login', {state: {from: location}, replace: true});
    } else if (response.status === 409) {
      setUpdateConflict(true);

    } else if (response?.status >= 200 && response?.status < 300) {
      const savedId = response.data?.id ?? item.id;
      let savedEmployee = response.data;

      if (savedId) {
        const refreshed = await getItems(`${dataFullUrl}/${savedId}`);
        if (refreshed?.status === 200) savedEmployee = refreshed.data;
      }

      if (savedEmployee) {
        const normalizedEmployee = {
          ...savedEmployee,
          bankAccountOwnedByEmployee: savedEmployee?.bankAccountOwnedByEmployee !== false
        };
        setCurrentItem(normalizedEmployee);
        setBackupItem(normalizedEmployee);
        if (typeof onEmbeddedSaved === "function") onEmbeddedSaved(normalizedEmployee);
      }

      setCurrentFormState(prev => ({
        ...prev,
        showForm: true,
        formAddingDataMode: false,
        formSaveButtonDisabled: true
      }));
      toastSuccess("Zapisano dane pracownika");
    }

  }

  const checkRelatedItems = async (employeeId) => {
    if (!canDeleteEmployees) return;

    const response = await getItems(`${dataFullUrl}/${employeeId}/deletion-check`);
    if (!response || response.status !== 200) {
      if (response?.status === 401 || response?.status === 403) {
        navigate('/login', {state: {from: location}, replace: true});
      } else {
        toastError("Nie udało się sprawdzić, czy pracownik może zostać usunięty.");
      }
      return;
    }

    const check = response.data;
    const employee = itemsList.find(item => item.id === employeeId);
    const employeeName = [employee?.firstName, employee?.lastName].filter(Boolean).join(" ") || employee?.login || `ID ${employeeId}`;

    if (check.canDelete) {
      const removable = (check.removableRelations || []).length > 0
        ? ` Usunięte zostaną również techniczne przypisania: ${check.removableRelations.join(", ")}.`
        : "";
      setCurrentFormState(prev => ({
        ...prev,
        showDeleteWarning: true,
        warningDescription: `Czy na pewno usunąć pracownika ${employeeName}?${removable} Tej akcji nie można cofnąć.`,
        warningDeleteButtonDisabled: false,
        warningWarningIconVisible: false
      }));
      return;
    }

    const reasons = (check.blockingRelations || []).join(", ");
    setCurrentFormState(prev => ({
      ...prev,
      showDeleteWarning: true,
      warningDescription: `Nie można usunąć pracownika ${employeeName}, ponieważ posiada dane historyczne lub operacyjne: ${reasons}.`,
      warningDeleteButtonDisabled: true,
      warningWarningIconVisible: true
    }));
  }

  const archiveCurrentEmployee = async () => {
    if (!currentItem?.id) return;
    const reason = window.prompt("Powód przeniesienia pracownika do archiwum (opcjonalnie):", currentItem.archivedReason || "");
    if (reason === null) return;
    try {
      await axiosPrivate.post(`${dataUrl}/${currentItem.id}/archive`, {reason});
      const refreshed = await getItems(dataUrl);
      if (refreshed.status === 200) setItems(refreshed.data);
      onCloseDetails({state});
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się zarchiwizować pracownika.");
    }
  };

  const restoreCurrentEmployee = async () => {
    if (!currentItem?.id) return;
    try {
      await axiosPrivate.post(`${dataUrl}/${currentItem.id}/restore`);
      const response = await axiosPrivate.get(`${dataUrl}/${currentItem.id}`);
      setCurrentItem(response.data);
      setBackupItem(response.data);
      const refreshed = await getItems(dataUrl);
      if (refreshed.status === 200) setItems(refreshed.data);
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się przywrócić pracownika z archiwum.");
    }
  };

  const onClose = () => {
    setUpdateConflict(false);
    onCloseDetails({ state });
    if (embedded && typeof onEmbeddedClose === "function") onEmbeddedClose();
  };


  useEffect(() => {
    if (updateConflict) setUpdateConflict(false);
    compareData(currentFormState, setCurrentFormState, currentItem, backupItem);
//    console.log("CURRENT ITEM:" + currentItem.firstName + "/" + currentItem.photos);
  }, [itemChanged])


  useEffect(() => {
    let active = true;

    const loadColumnPreferences = async () => {
      try {
        const response = await axiosPrivate.get(employeeColumnPreferencesUrl);
        if (!active) return;
        if (Array.isArray(response.data)) {
          setVisibleColumnAccessors(response.data);
        }
      } catch (_) {
        if (active) setVisibleColumnAccessors(defaultVisibleColumnAccessors);
      }
    };

    loadColumnPreferences();
    return () => { active = false; };
  }, [auth?.user]);

  useEffect(() => {
    const getData = async () => {
      const itemsData = await getItems(dataUrl);

      if (itemsData.status === 401 || itemsData.status === 403) {
        navigate('/login', {state: {from: location}, replace: true});
        return;
      }

      if (itemsData.status !== 200) {
        setLoading(false);
        toastError("Nie udało się pobrać listy pracowników");
        return;
      }

      setItems(itemsData.data);

      const lookups = [
        ...(canDeleteEmployees ? [["role", rolesUrl, setRolesList]] : []),
        ["statusy cywilne", maritalStatusesUrl, setMaritalStatuses],
        ["płcie", gendersUrl, setGenders],
        ["oddziały NFZ", nfzBranchesUrl, setNfzBranches],
        ["urzędy skarbowe", taxOfficesUrl, setTaxOffices],
        ["etapy zatrudnienia", hiringStagesUrl, setHiringStages],
        ["kraje", countriesUrl, setCountries],
        ["typy dokumentów", identityCardTypesUrl, setIdentityCardTypes],
        ["ustawienia", settingsUrl, setSettingsList],
        ["umiejętności", softSkillsUrl, setSoftSkillsList],
        ["szkolenia", trainingsUrl, setTrainingsList],
        ["pochwały", commendationsUrl, setCommendationsList],
        ["przewinienia", infractionsUrl, setInfractionsList],
        ["typy certyfikatów", certificationTypesUrl, setCertificationTypesList],
        ["typy badań", medexamTypesUrl, setMedExamTypesList],
        ["typy nieobecności", absenceTypesUrl, setAbsenceTypesList],
        ["działy", departmentsUrl, setDepartmentsList],
        ["stanowiska", positionsUrl, setPositionsList],
        ["firmy zatrudniające", hiringCompaniesUrl, setHiringCompaniesList],
        ["karty RCP", timeCardsUrl, setTimeCardsList]
      ];

      const responses = await Promise.all(lookups.map(([, url]) => getItems(url)));
      const failed = [];

      responses.forEach((response, index) => {
        const [label, , setter] = lookups[index];
        if (response.status === 200) {
          setter(response.data);
        } else if (response.status === 401 || response.status === 403) {
          navigate('/login', {state: {from: location}, replace: true});
        } else {
          failed.push(label);
        }
      });

      setLoading(false);
      if (failed.length > 0) {
        toastError(`Nie udało się pobrać danych pomocniczych: ${failed.join(", ")}. Lista pracowników pozostaje dostępna.`);
      }
    };

    getData();
  }, [location.key])


  useEffect(() => {
    if (id) {
      const loadItem = async () => {
        const response = await getItems(`${dataUrl}/${id}`);
        if (response.status === 200) {
          const item = {
            ...response.data,
            bankAccountOwnedByEmployee: response.data?.bankAccountOwnedByEmployee !== false
          };
          setCurrentItem(item);
          setBackupItem(item);
          setCurrentFormState({
            ...defaultFormState,
            showForm: true,
            formAddingDataMode: false,
            formHeader: "Edycja pracownika",
            formDescription: "Edytujesz istniejącego pracownika"
          });
          setLoading(false);
        } else if (response.status === 401 || response.status === 403) {
          navigate('/login', { state: { from: location }, replace: true });
        } else {
          toastError("Nie udało się pobrać danych pracownika.");
          if (embedded && typeof onEmbeddedClose === "function") onEmbeddedClose();
        }
      };
      loadItem();
    }
  }, [id]);






  const getCompleteItem = async (id) => {
    const response = await getItems(`${dataFullUrl}/${id}`);
    if (response.status === 200) {
      let item = {
        ...response.data,
        bankAccountOwnedByEmployee: response.data?.bankAccountOwnedByEmployee !== false
      };
      item.timeCard = timeCardsList.find(tc => tc.id === item.timeCardId)
      return item;
    } else if (response.status === 401 || response.status === 403) {
      navigate('/login', { state: { from: location }, replace: true})
    } else {
      toastError("Nie udało się pobrać danych.");
    }
  }


  const addDataButtonProps = {
    setCurrentItem,
    setBackupItem,
    defaultItem: defaultFullItem,
    currentFormState,
    setCurrentFormState,
    formDescription: ``,
    formHeader: `Nowy ${ itemName }`,
    buttonTitle: `Dodaj`
  }


  const refreshEmployeesList = async () => {
    const response = await getItems(dataUrl);
    if (response?.status === 200) {
      setItems(response.data);
      return;
    }
    toastError("Nie udało się odświeżyć listy pracowników po zmianie przypisań.");
  };


  const state = {
    itemsList, setItems,
    currentItem, setCurrentItem,
    currentFormState, setCurrentFormState,
    defaultItem, backupItem, setBackupItem, itemChanged, setItemChanged,
    setAllowDelete, validPassword, setValidPassword,
    maritalStatusesList, setMaritalStatuses,
    gendersList, setGenders,
    nfzBranchesList, setNfzBranches,
    taxOfficesList, setTaxOffices,
    hiringStagesList, setHiringStages,
    countriesList, setCountries,
    identityCardTypesList, setIdentityCardTypes,
    hiringCompaniesList, setHiringCompaniesList,
    showDeptGuard, setShowDeptGuard,
    timeCardsList, setTimeCardsList
  }


  const employeeTableState = {
    ...state,
    itemsList: itemsList
      .filter(item => archiveFilter === "ALL" || (archiveFilter === "ARCHIVED" ? !!item.archived : !item.archived))
      .filter(item => managementFilter === "ALL" || (managementFilter === "MANAGEMENT" ? !!item.managementPosition : !item.managementPosition))
      .map(prepareEmployeeListItem)
  };

  const visibleEmployees = employeeTableState.itemsList;

  let dataSectionContent;
  if (loading) {
    dataSectionContent = <LoadingDataDiv />
  } else if (visibleEmployees.length > 0) {
    dataSectionContent =
      <Table
        columns = { columns }
        state = { employeeTableState }
        tableClassName="employee-table"
        tableContainerClassName="employee-table-scroll"
        checkRelatedItems = { checkRelatedItems }
        formHeader = {`Edycja pracownika`}
        // relatedItemsUrl = { userClientsUrl }
        getCompleteItem = { getCompleteItem }
        exportDataFactory={exportEmployeeRows}
        exportFileName={archiveFilter === "ARCHIVED" ? "pracownicy_archiwum.xlsx" : archiveFilter === "ALL" ? "pracownicy_wszyscy.xlsx" : "pracownicy.xlsx"}
      />
    // console.log(itemsList);
  } else {
    const emptyArchiveLabel = archiveFilter === "ARCHIVED"
      ? "Brak zarchiwizowanych pracowników."
      : archiveFilter === "ALL"
        ? "Nie znaleziono pracowników."
        : "Nie znaleziono aktywnych pracowników.";
    dataSectionContent = <div className="warn_info">{emptyArchiveLabel}</div>
  }


  const handleNavigation = (e, sectionId, tabKey) => {
    e.preventDefault();
    setActiveTab(tabKey);

    setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }, 80);
  };



  const restoreEmployeeValues = () => {
    setUpdateConflict(false);
    const pwdField = document.getElementById("password");
    const pwdConfirmationField = document.getElementById("passwordConf");
    const oldPwdField = document.getElementById("oldPassword");
    if (pwdField) pwdField.value = null;
    if (pwdConfirmationField) pwdConfirmationField.value = null;
    if (oldPwdField) oldPwdField.value = null;
    restoreFormData({state});
  };

  const editorTitle = currentFormState.formAddingDataMode
    ? "Nowy pracownik"
    : `Edycja pracownika: ${currentItem.login || `${currentItem.firstName || ""} ${currentItem.lastName || ""}`.trim() || "bez loginu"}`;

  return (
    <div id={embedded ? undefined : "layoutSidenav_content"} className={`dictionary-page resource-page ${embedded ? "employee-embedded-page" : ""}`.trim()}>
      <ToastContainer/>
      {!embedded && (currentFormState.showForm ? (
        <AppComponentCardHeader title="Pracownicy"/>
      ) : (
        <AppComponentCardHeader title="Pracownicy" buttonProps={addDataButtonProps}/>
      ))}

      {!embedded && !currentFormState.showForm && canDeleteEmployees && (
        <EmployeeAssignmentTools onChanged={refreshEmployeesList}/>
      )}

      <div className="container-fluid px-4 py-3">
        {currentFormState.showForm ? (
          <div className="resource-editor-shell resource-employee-editor">
            <div className="resource-editor-topbar">
              <div>
                <strong>{editorTitle}</strong>
                <span>Kartoteka pracownika, dane firmowe, kwalifikacje, dostępność i historia pracy.</span>
              </div>
              <div className="resource-editor-actions">
                <p className="text-center w-responsive mx-auto mb-0 data_changed" id="data-changed">
                  <FontAwesomeIcon icon={faExclamationCircle}/>&nbsp;{currentFormState.formDataChangedWarning}
                </p>
                <Button size="sm" variant="outline-secondary" onClick={restoreEmployeeValues}>
                  Przywróć wartości
                </Button>
                {canArchiveEmployees && !currentFormState.formAddingDataMode && (
                  currentItem.archived
                    ? <Button size="sm" variant="outline-success" onClick={restoreCurrentEmployee}>Przywróć z archiwum</Button>
                    : <Button size="sm" variant="outline-warning" onClick={archiveCurrentEmployee}>Przenieś do archiwum</Button>
                )}
              </div>
            </div>

            <div className="resource-editor-body">
              <form id="employee-form" name="employee-form">
                <Tabs activeKey={activeTab} onSelect={key => setActiveTab(key)} id="employee" className="mb-3">
                  <Tab eventKey="employee-personal-data" title="Dane osobowe">
                    <EmployeeTabQuickAccess
                      items={PERSONAL_QUICK_LINKS}
                      tabKey="employee-personal-data"
                      onNavigate={handleNavigation}
                    />
                    <TabEmployeePersonalData state={state} rolesList={rolesList}/>
                  </Tab>
                  <Tab eventKey="employee-company-data" title="Dane firmowe" key="employee-company-data">
                    <EmployeeTabQuickAccess
                      items={currentFormState.formAddingDataMode ? COMPANY_QUICK_LINKS.filter(item => ["employment-organization", "positions"].includes(item.id)) : COMPANY_QUICK_LINKS}
                      tabKey="employee-company-data"
                      onNavigate={handleNavigation}
                    />
                    <TabEmployeeCompanyData
                      state={state}
                      rolesList={rolesList}
                      softSkillsList={softSkillsList}
                      trainingsList={trainingsList}
                      commendationsList={commendationsList}
                      infractionsList={infractionsList}
                      certificationTypesList={certificationTypesList}
                      medExamTypesList={medExamTypesList}
                      absenceTypesList={absenceTypesList}
                      supervisorsList={itemsList.map(item => ({
                        id: item.id,
                        login: item.login,
                        firstName: item.firstName,
                        lastName: item.lastName,
                        permission: item.roles && item.roles.includes("modify")
                      }))}
                      departmentsList={departmentsList}
                      positionsList={positionsList}
                      settings={settingsList}
                      canManageRoles={canDeleteEmployees}
                    />
                  </Tab>
                  {!currentFormState.formAddingDataMode && ([
                    <Tab eventKey="employee-work_log" title="Historia pracy" key="employee-work-log">
                      <EmployeeTabQuickAccess
                        items={[{id: "employee-work-history", label: "Historia pracy"}]}
                        tabKey="employee-work_log"
                        onNavigate={handleNavigation}
                      />
                      <div id="employee-work-history">
                        <TabEmployeeWorkLog employeeId={currentItem.id}/>
                      </div>
                    </Tab>,
                    ...(canViewEmployeeDocuments ? [
                      <Tab eventKey="employee-documents" title="Dokumenty" key="employee-documents">
                        <EmployeeTabQuickAccess
                          items={[{id: "employee-documents", label: "Dokumenty"}]}
                          tabKey="employee-documents"
                          onNavigate={handleNavigation}
                        />
                        <div id="employee-documents">
                          <EmployeeDocumentsCard employeeId={currentItem.id} requiresBankAccountConsent={currentItem.bankAccountOwnedByEmployee === false} canManage={canManageEmployeeDocuments}/>
                        </div>
                      </Tab>
                    ] : [])
                  ])}
                </Tabs>
              </form>

              {updateConflict && (
                <div className="dictionary-form-error">
                  <FontAwesomeIcon icon={faInfoCircle}/>
                  <span>Nie można dodać ani zmienić pracownika, ponieważ istnieje już wpis o tej nazwie lub skrócie.</span>
                </div>
              )}
            </div>

            <div className="modal-controls employee-editor-controls">
              <div className="d-flex w-100 align-items-center justify-content-end gap-2">
                {canDeleteEmployees && !currentFormState.formAddingDataMode && !embedded && (
                  <Button variant="outline-danger" onClick={() => checkRelatedItems(currentItem.id)}>Usuń</Button>
                )}
                <Button variant="secondary" id="btn-close" onClick={onClose}>Zamknij</Button>
                <Button id="save-and-close-button" variant="primary" onClick={onSaveItemClick} disabled={currentFormState.formSaveButtonDisabled}>
                  Zapisz
                </Button>
              </div>
            </div>
          </div>
        ) : embedded ? (
          <div className="p-4"><LoadingDataDiv/></div>
        ) : (
          <div className="card shadow-sm mb-5 resource-list-card resource-employee-list-card">
            <div className="p-3 border-bottom d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <label htmlFor="employee-archive-filter" className="small text-muted mb-0">Archiwum</label>
                <Form.Select
                  size="sm"
                  id="employee-archive-filter"
                  value={archiveFilter}
                  onChange={event => setArchiveFilter(event.target.value)}
                  style={{minWidth: "150px"}}
                >
                  <option value="ACTIVE">Aktywni</option>
                  <option value="ARCHIVED">Archiwalni</option>
                  <option value="ALL">Wszyscy</option>
                </Form.Select>
                <label htmlFor="employee-management-filter" className="small text-muted mb-0 ms-2">Stanowiska</label>
                <Form.Select
                  size="sm"
                  id="employee-management-filter"
                  value={managementFilter}
                  onChange={event => setManagementFilter(event.target.value)}
                  style={{minWidth: "175px"}}
                >
                  <option value="ALL">Wszystkie</option>
                  <option value="MANAGEMENT">Kierownicze</option>
                  <option value="NON_MANAGEMENT">Niekierownicze</option>
                </Form.Select>
                <span className="text-muted small">Widocznych: {visibleEmployees.length}</span>
              </div>
              <Button size="sm" variant="outline-secondary" onClick={() => setShowColumnPicker(true)}>Kolumny</Button>
            </div>
            {dataSectionContent}
          </div>
        )}
      </div>

      <DeleteWarningModal state={state} onDelete={onDelete} deleteItemName={itemName}/>

      <Modal show={showColumnPicker} onHide={() => setShowColumnPicker(false)} centered size="sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title as="h6">Widoczne kolumny</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-2">
          <div className="row g-1">
            {configurableColumns.map(column => (
              <div className="col-6" key={column.accessor}>
                <Form.Check
                  type="checkbox"
                  id={`employee-column-${column.accessor}`}
                  label={column.label}
                  checked={visibleColumnAccessors.includes(column.accessor)}
                  onChange={() => toggleEmployeeColumn(column.accessor)}
                />
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button size="sm" variant="secondary" onClick={() => setShowColumnPicker(false)}>Zamknij</Button>
        </Modal.Footer>
      </Modal>

    </div>
  );


}

export default Employee;

